import {
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { toast } from "react-toastify";
import Input from "../../../Shared/Input";
import TextArea from "../../../Shared/TextArea";
import Selectbox, { option } from "../../../Shared/SelectBox";
import Upload from "../../../Shared/Upload";
import TrashIcon from "../../../Icons/TrashIcon";
import {
  CBQQuestionType,
  InitCBQQuestion,
} from "../../../../api/Quiz/InitCBQQuestion";
import { InitCBQExam } from "../../../../api/Quiz/InitCBQExam";
import { UpdateCBQExam } from "../../../../api/Quiz/UpdateCBQExam";
import { InitCBQScenario } from "../../../../api/Quiz/InitCBQScenario";
import { UpdateCBQScenario } from "../../../../api/Quiz/UpdateCBQScenario";
import { UpdateCBQQuestion } from "../../../../api/Quiz/UpdateCBQQuestion";
import { DeleteCBQScenario } from "../../../../api/Quiz/DeleteCBQScenario";
import { DeleteCBQQuestion } from "../../../../api/Quiz/DeleteCBQQuestion";
import { ReorderCBQQuestions } from "../../../../api/Quiz/ReorderCBQQuestions";
import { ReorderCBQScenarios } from "../../../../api/Quiz/ReorderCBQScenarios";
import LocalQuestionEditor from "./LocalQuestionEditor";
import {
  DraftCBQExam,
  DraftQuestion,
  CBQ_MODE_OPTIONS,
  makeDraftQuestion,
  makeDraftScenario,
} from "./types";

const QUESTION_TYPE_OPTIONS: { value: CBQQuestionType; label: string }[] = [
  { value: "mcq", label: "Multiple Choice" },
  { value: "multi_response", label: "Multiple Response" },
  { value: "drag_drop_ordering", label: "Drag & Drop — Ordering" },
  { value: "drag_drop_classification", label: "Drag & Drop — Classification" },
  { value: "dropdown", label: "Dropdown / Fill-in" },
  { value: "numeric", label: "Numeric" },
];

// Parent passes React's state dispatch so async callbacks can update against the
// freshest draft via the functional form (avoids clobbering concurrent edits).
type DraftUpdater = DraftCBQExam | ((prev: DraftCBQExam) => DraftCBQExam);

interface CBQBuilderProps {
  value: DraftCBQExam;
  onChange: (updater: DraftUpdater) => void;
  mcqQuizzes?: { id: string; title: string }[];
  lectureId?: string;
  // Edit mode: the exam already exists server-side, so `ensureExamId` must
  // resolve to it instead of minting a new draft exam via InitCBQExam.
  existingExamId?: string;
  // Rendered inside Exam Details, above the title. Edit mode uses it for the
  // lecture link, which the create flow picks in an earlier step instead.
  beforeTitle?: ReactNode;
}

// Imperative handle the parent uses at publish time.
export interface CBQBuilderHandle {
  // Flush any debounced text patches so the server has the latest before publish.
  flushPending: () => Promise<void>;
  getExamId: () => string | undefined;
}

const errText = (e: { message?: string } | null | undefined, fallback: string) =>
  e?.message || fallback;

const CBQBuilder = forwardRef<CBQBuilderHandle, CBQBuilderProps>(
  (
    {
      value,
      onChange,
      mcqQuizzes = [],
      lectureId,
      existingExamId,
      beforeTitle,
    },
    ref
  ) => {
    // Per-scenario "next question type" picker + per-scenario upload flag.
    const [newType, setNewType] = useState<Record<string, CBQQuestionType>>({});
    const [uploading, setUploading] = useState<Record<string, boolean>>({});

    // Latest draft, for reads inside async closures / debounced callbacks.
    const valueRef = useRef(value);
    valueRef.current = value;

    // Promise of each entity's serverId (keyed by tempId). Lets an action fired
    // before its init resolves still await the real id.
    // Pre-resolved in edit mode so ensureExamId short-circuits instead of
    // creating a second exam.
    const examIdRef = useRef<Promise<string> | null>(
      existingExamId ? Promise.resolve(existingExamId) : null
    );
    const scenarioIdRef = useRef<Record<string, Promise<string>>>({});
    const questionIdRef = useRef<Record<string, Promise<string>>>({});

    // Debounced text patches: key -> timer + pending fn (fn kept for flush).
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const pending = useRef<Record<string, () => void | Promise<void>>>({});

    const debounce = (
      key: string,
      fn: () => void | Promise<void>,
      ms = 600
    ) => {
      pending.current[key] = fn;
      if (timers.current[key]) clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(async () => {
        delete timers.current[key];
        const f = pending.current[key];
        delete pending.current[key];
        await f?.();
      }, ms);
    };

    const flushPending = async () => {
      for (const k of Object.keys(pending.current)) {
        if (timers.current[k]) clearTimeout(timers.current[k]);
        delete timers.current[k];
        const f = pending.current[k];
        delete pending.current[k];
        await f?.();
      }
    };

    useImperativeHandle(ref, () => ({
      flushPending,
      getExamId: () => valueRef.current.examId,
    }));

    const buildExamPayload = (v: DraftCBQExam) => ({
      title: v.title.trim(),
      description: v.description.trim(),
      mode: v.mode,
      lectureId,
      ...(v.mode === "PROD_WITH_MCQ"
        ? {
            quizId: v.quizId || undefined,
            requiredQuizScore: v.requiredQuizScore.trim()
              ? Number(v.requiredQuizScore)
              : undefined,
          }
        : {}),
    });

    // Create the draft exam on the server the first time one is needed (i.e. the
    // first scenario add). Cached so it only ever fires once.
    const ensureExamId = (): Promise<string> => {
      if (!examIdRef.current) {
        examIdRef.current = (async () => {
          const res = await InitCBQExam();
          if (res.error || !res.data) {
            examIdRef.current = null;
            throw new Error(errText(res.error, "Failed to create CBQ exam"));
          }
          const examId = res.data.id;
          onChange((prev) => ({ ...prev, examId }));
          // Persist whatever exam meta is already typed (incl. lectureId).
          const patch = await UpdateCBQExam({
            id: examId,
            payload: buildExamPayload(valueRef.current),
          });
          if (patch.error)
            toast.error(errText(patch.error, "Failed to save CBQ exam"));
          return examId;
        })();
      }
      return examIdRef.current;
    };

    const getScenarioId = (tempId: string): Promise<string> => {
      const p = scenarioIdRef.current[tempId];
      if (p) return p;
      const s = valueRef.current.scenarios.find((x) => x.tempId === tempId);
      if (s?.serverId) return Promise.resolve(s.serverId);
      return Promise.reject(new Error("scenario not created"));
    };

    const getQuestionId = (tempId: string): Promise<string> => {
      const p = questionIdRef.current[tempId];
      if (p) return p;
      for (const s of valueRef.current.scenarios) {
        const q = s.questions.find((x) => x.tempId === tempId);
        if (q?.serverId) return Promise.resolve(q.serverId);
      }
      return Promise.reject(new Error("question not created"));
    };

    const findQuestion = (tempId: string): DraftQuestion | undefined => {
      for (const s of valueRef.current.scenarios) {
        const q = s.questions.find((x) => x.tempId === tempId);
        if (q) return q;
      }
      return undefined;
    };

    // ----- Exam -----

    const editExam = (patch: Partial<DraftCBQExam>) => {
      onChange((prev) => ({ ...prev, ...patch }));
      // Before the exam exists it has no id to patch; its meta is sent in full
      // by ensureExamId when the first scenario is added.
      if (!valueRef.current.examId && !examIdRef.current) return;
      debounce("exam", async () => {
        const examId = await ensureExamId();
        const r = await UpdateCBQExam({
          id: examId,
          payload: buildExamPayload(valueRef.current),
        });
        if (r.error) toast.error(errText(r.error, "Failed to save CBQ exam"));
      });
    };

    // ----- Scenarios -----

    const addScenario = () => {
      const draft = makeDraftScenario();
      onChange((prev) => ({ ...prev, scenarios: [...prev.scenarios, draft] }));
      const p = (async () => {
        const examId = await ensureExamId();
        const res = await InitCBQScenario(examId);
        if (res.error || !res.data)
          throw new Error(errText(res.error, "Failed to add scenario"));
        const sid = res.data.id;
        onChange((prev) => ({
          ...prev,
          scenarios: prev.scenarios.map((s) =>
            s.tempId === draft.tempId ? { ...s, serverId: sid } : s
          ),
        }));
        return sid;
      })();
      p.catch((e: Error) => toast.error(e.message));
      scenarioIdRef.current[draft.tempId] = p;
    };

    const patchScenario = (
      index: number,
      patch: Partial<{
        title: string;
        estimatedMinutes: string;
        pdfS3Key: string;
      }>
    ) => {
      const tempId = valueRef.current.scenarios[index].tempId;
      onChange((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((s, i) =>
          i === index ? { ...s, ...patch } : s
        ),
      }));
      debounce(`sc-${tempId}`, async () => {
        let sid: string;
        try {
          sid = await getScenarioId(tempId);
        } catch {
          return;
        }
        const s = valueRef.current.scenarios.find((x) => x.tempId === tempId);
        if (!s) return;
        const minutes = s.estimatedMinutes.trim()
          ? Number(s.estimatedMinutes)
          : undefined;
        const r = await UpdateCBQScenario({
          scenarioId: sid,
          payload: {
            title: s.title.trim(),
            pdfS3Key: s.pdfS3Key.trim(),
            ...(minutes !== undefined ? { estimatedMinutes: minutes } : {}),
          },
        });
        if (r.error) toast.error(errText(r.error, "Failed to save scenario"));
      });
    };

    const removeScenario = (index: number) => {
      const sc = valueRef.current.scenarios[index];
      onChange((prev) => ({
        ...prev,
        scenarios: prev.scenarios.filter((_, i) => i !== index),
      }));
      delete pending.current[`sc-${sc.tempId}`];
      sc.questions.forEach((q) => {
        delete pending.current[`q-${q.tempId}`];
        delete questionIdRef.current[q.tempId];
      });
      const p = getScenarioId(sc.tempId);
      delete scenarioIdRef.current[sc.tempId];
      p.then(async (sid) => {
        const r = await DeleteCBQScenario(sid);
        if (r.error) toast.error(errText(r.error, "Failed to delete scenario"));
      }).catch(() => {
        /* never created; nothing to delete */
      });
    };

    const moveScenario = (index: number, dir: -1 | 1) => {
      const target = index + dir;
      const current = valueRef.current.scenarios;
      if (target < 0 || target >= current.length) return;
      const scenarios = [...current];
      [scenarios[index], scenarios[target]] = [
        scenarios[target],
        scenarios[index],
      ];
      onChange((prev) => {
        const next = [...prev.scenarios];
        [next[index], next[target]] = [next[target], next[index]];
        return { ...prev, scenarios: next };
      });
      // Same anchoring rule as moveQuestion: afterId normally, beforeId when
      // the scenario lands first.
      const movedTempId = current[index].tempId;
      const anchorTempId =
        target > 0 ? scenarios[target - 1].tempId : scenarios[1]?.tempId;
      (async () => {
        try {
          const examId = await ensureExamId();
          const movedId = await getScenarioId(movedTempId);
          if (!anchorTempId) return; // single scenario — nothing to anchor to
          const anchorId = await getScenarioId(anchorTempId);
          const r = await ReorderCBQScenarios(examId, [
            target > 0
              ? { id: movedId, afterId: anchorId }
              : { id: movedId, beforeId: anchorId },
          ]);
          if (r.error) toast.error(errText(r.error, "Failed to reorder"));
        } catch {
          /* ids not ready / failed — local order stands */
        }
      })();
    };

    // ----- Questions -----

    const addQuestion = (sIndex: number) => {
      const scenario = valueRef.current.scenarios[sIndex];
      const type = newType[scenario.tempId] ?? "mcq";
      const draft = makeDraftQuestion(type);
      onChange((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((s, i) =>
          i === sIndex ? { ...s, questions: [...s.questions, draft] } : s
        ),
      }));
      const p = (async () => {
        const sid = await getScenarioId(scenario.tempId);
        const res = await InitCBQQuestion(sid, type);
        if (res.error || !res.data)
          throw new Error(errText(res.error, "Failed to add question"));
        const qid = res.data.id;
        onChange((prev) => ({
          ...prev,
          scenarios: prev.scenarios.map((s) =>
            s.tempId === scenario.tempId
              ? {
                  ...s,
                  questions: s.questions.map((q) =>
                    q.tempId === draft.tempId ? { ...q, serverId: qid } : q
                  ),
                }
              : s
          ),
        }));
        return qid;
      })();
      p.catch((e: Error) => toast.error(e.message));
      questionIdRef.current[draft.tempId] = p;
    };

    const editQuestion = (
      sIndex: number,
      qIndex: number,
      next: DraftQuestion
    ) => {
      onChange((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((s, i) =>
          i === sIndex
            ? {
                ...s,
                questions: s.questions.map((q, j) => (j === qIndex ? next : q)),
              }
            : s
        ),
      }));
      debounce(`q-${next.tempId}`, async () => {
        let qid: string;
        try {
          qid = await getQuestionId(next.tempId);
        } catch {
          return;
        }
        const cur = findQuestion(next.tempId);
        if (!cur) return;
        // multi_response always needs requiredCount; default to 0 if untouched.
        const correctAnswerJson =
          cur.type === "multi_response" &&
          (cur.correctAnswerJson as { requiredCount?: number })
            .requiredCount === undefined
            ? { ...cur.correctAnswerJson, requiredCount: 0 }
            : cur.correctAnswerJson;
        const r = await UpdateCBQQuestion({
          questionId: qid,
          payload: {
            prompt: cur.prompt.trim(),
            helpText: cur.helpText.trim(),
            optionsJson: cur.optionsJson,
            correctAnswerJson,
            points: cur.points,
          },
        });
        if (r.error) toast.error(errText(r.error, "Failed to save question"));
      });
    };

    const removeQuestion = (sIndex: number, qIndex: number) => {
      const q = valueRef.current.scenarios[sIndex].questions[qIndex];
      onChange((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((s, i) =>
          i === sIndex
            ? { ...s, questions: s.questions.filter((_, j) => j !== qIndex) }
            : s
        ),
      }));
      delete pending.current[`q-${q.tempId}`];
      // Resolve before dropping the ref: in edit mode the id lives on
      // q.serverId (no in-session init promise), so reading the ref alone
      // would skip the server delete entirely.
      const p = getQuestionId(q.tempId);
      delete questionIdRef.current[q.tempId];
      p.then(async (qid) => {
        const r = await DeleteCBQQuestion(qid);
        if (r.error) toast.error(errText(r.error, "Failed to delete question"));
      }).catch(() => {
        /* never created; nothing to delete */
      });
    };

    const moveQuestion = (sIndex: number, qIndex: number, dir: -1 | 1) => {
      const scenario = valueRef.current.scenarios[sIndex];
      const target = qIndex + dir;
      if (target < 0 || target >= scenario.questions.length) return;
      const questions = [...scenario.questions];
      [questions[qIndex], questions[target]] = [
        questions[target],
        questions[qIndex],
      ];
      onChange((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((s, i) =>
          i === sIndex ? { ...s, questions } : s
        ),
      }));
      // Persist: anchor the moved question to a sibling in its new position.
      // Landing first means there is no predecessor, so anchor on the follower
      // with beforeId instead — the endpoint takes one or the other, not both.
      const movedTempId = scenario.questions[qIndex].tempId;
      const anchorTempId =
        target > 0 ? questions[target - 1].tempId : questions[1]?.tempId;
      (async () => {
        try {
          const sid = await getScenarioId(scenario.tempId);
          const movedId = await getQuestionId(movedTempId);
          if (!anchorTempId) return; // single question — nothing to anchor to
          const anchorId = await getQuestionId(anchorTempId);
          const r = await ReorderCBQQuestions(sid, [
            target > 0
              ? { id: movedId, afterId: anchorId }
              : { id: movedId, beforeId: anchorId },
          ]);
          if (r.error) toast.error(errText(r.error, "Failed to reorder"));
        } catch {
          /* ids not ready / failed — local order stands */
        }
      })();
    };

    const selectedMode =
      CBQ_MODE_OPTIONS.find((m) => m.id === value.mode) || CBQ_MODE_OPTIONS[0];

    const quizOptions = mcqQuizzes.map((q) => ({ id: q.id, name: q.title }));
    const selectedQuiz = quizOptions.find((q) => q.id === value.quizId) || null;

    return (
      <div className="w-full max-w-3xl mx-auto space-y-10">
        {/* Exam details */}
        <section className="space-y-5">
          <div>
            <h2 className="font-bold text-xl text-burntBlack">Exam Details</h2>
            <p className="text-sm text-gray-500 mt-1">
              Title and mode for this CBQ exam.
            </p>
          </div>
          {beforeTitle}
          <Input
            title="Title"
            name="cbq-title"
            placeholder="e.g. CMA Part 2 - May 2026"
            value={value.title}
            onChange={(e) => editExam({ title: e.target.value })}
          />
          <TextArea
            title="Description"
            name="cbq-description"
            placeholder="Optional description"
            value={value.description}
            onChange={(e) => editExam({ description: e.target.value })}
          />
          <Selectbox
            title="Mode"
            placeholder="Select mode"
            selectOptions={CBQ_MODE_OPTIONS}
            selected={selectedMode}
            setSelected={
              ((opt: option | null) =>
                editExam({
                  mode: opt?.id || CBQ_MODE_OPTIONS[0].id,
                })) as React.Dispatch<React.SetStateAction<option | null>>
            }
          />

          {value.mode === "PROD_WITH_MCQ" && (
            <>
              <Selectbox
                title="MCQ Quiz "
                placeholder={
                  quizOptions.length
                    ? "Select an MCQ quiz"
                    : "No MCQ quizzes on this lecture"
                }
                selectOptions={quizOptions}
                selected={selectedQuiz}
                setSelected={
                  ((opt: option | null) =>
                    editExam({ quizId: opt?.id || "" })) as React.Dispatch<
                    React.SetStateAction<option | null>
                  >
                }
              />
              <Input
                title="Required Quiz Score (%)"
                name="cbq-required-score"
                type="number"
                step="any"
                placeholder="e.g. 50"
                value={value.requiredQuizScore}
                onChange={(e) => editExam({ requiredQuizScore: e.target.value })}
              />
            </>
          )}
        </section>

        {/* Scenarios */}
        <section className="space-y-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-bold text-xl text-burntBlack">Scenarios</h2>
              <p className="text-sm text-gray-500 mt-1">
                Each scenario holds a PDF and its questions.
              </p>
            </div>
            {value.scenarios.length > 0 && (
              <span className="inline-flex items-center rounded-full bg-[#4379EE]/10 text-[#4379EE] px-3 py-1 text-xs font-bold">
                {value.scenarios.length}{" "}
                {value.scenarios.length === 1 ? "scenario" : "scenarios"}
              </span>
            )}
          </div>

          {value.scenarios.length === 0 ? (
            <p className="text-sm text-gray-500 py-10 text-center border-2 border-dashed border-[#4379EE]/25 rounded-2xl bg-[#F5F8FF]">
              No scenarios yet — add your first one below.
            </p>
          ) : (
            <div className="space-y-7">
              {value.scenarios.map((scenario, sIndex) => (
                <div
                  key={scenario.tempId}
                  className="rounded-2xl border border-[#4379EE]/20 bg-white shadow-sm overflow-hidden"
                >
                  {/* Scenario header band */}
                  <div className="flex items-center justify-between gap-3 bg-[#4379EE]/5 border-b border-[#4379EE]/15 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 shrink-0 rounded-full bg-[#4379EE] text-white font-bold flex items-center justify-center">
                        {sIndex + 1}
                      </span>
                      <h3 className="font-bold text-lg text-burntBlack">
                        {scenario.title?.trim()
                          ? scenario.title
                          : `Scenario ${sIndex + 1}`}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => moveScenario(sIndex, -1)}
                        disabled={sIndex === 0}
                        className="p-1.5 rounded-md text-gray-400 hover:text-[#4379EE] hover:bg-[#4379EE]/10 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Move scenario up"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveScenario(sIndex, 1)}
                        disabled={sIndex === value.scenarios.length - 1}
                        className="p-1.5 rounded-md text-gray-400 hover:text-[#4379EE] hover:bg-[#4379EE]/10 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Move scenario down"
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeScenario(sIndex)}
                        className="flex rounded-lg justify-center items-center border border-black/10 p-2 bg-white hover:bg-red-50 hover:border-red-200 transition-colors"
                        title="Remove scenario"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>

                  {/* Scenario meta */}
                  <div className="p-6 space-y-5">
                    <Input
                      title="Title"
                      name={`scenario-title-${scenario.tempId}`}
                      placeholder="e.g. Scenario 1 - XYZ Corp Q3 results"
                      value={scenario.title}
                      onChange={(e) =>
                        patchScenario(sIndex, { title: e.target.value })
                      }
                    />
                    <Input
                      title="Estimated Minutes"
                      name={`scenario-minutes-${scenario.tempId}`}
                      type="number"
                      step="any"
                      placeholder="e.g. 30"
                      value={scenario.estimatedMinutes}
                      onChange={(e) =>
                        patchScenario(sIndex, {
                          estimatedMinutes: e.target.value,
                        })
                      }
                    />
                    <div className="w-full">
                      <label className="block mb-2 font-medium text-burntBlack">
                        PDF <span className="text-red-500">*</span>
                      </label>
                      <Upload
                        folder="cbq/scenarios"
                        acceptedFiles={{ "application/pdf": [".pdf"] }}
                        onUploadStarts={() =>
                          setUploading((u) => ({
                            ...u,
                            [scenario.tempId]: true,
                          }))
                        }
                        onUploadEnds={() =>
                          setUploading((u) => ({
                            ...u,
                            [scenario.tempId]: false,
                          }))
                        }
                        onFileUploaded={(url, key) => {
                          patchScenario(sIndex, { pdfS3Key: key || url });
                          setUploading((u) => ({
                            ...u,
                            [scenario.tempId]: false,
                          }));
                        }}
                      >
                        <Input
                          name={`scenario-pdf-${scenario.tempId}`}
                          type="text"
                          value={scenario.pdfS3Key}
                          placeholder={
                            uploading[scenario.tempId]
                              ? "Uploading..."
                              : "Click to upload PDF"
                          }
                          inputStyles="!rounded !h-[50px]"
                          upload={true}
                        />
                      </Upload>
                    </div>

                    {/* Questions zone */}
                    <div className="rounded-xl bg-[#F5F8FF] border border-[#4379EE]/15 p-5 space-y-4">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-burntBlack">Questions</h4>
                        <span className="inline-flex items-center rounded-full bg-[#4379EE]/10 text-[#4379EE] px-2.5 py-0.5 text-xs font-bold">
                          {scenario.questions.length}
                        </span>
                      </div>

                      {scenario.questions.length === 0 ? (
                        <p className="text-sm text-gray-500 py-6 text-center border-2 border-dashed border-[#4379EE]/20 rounded-xl bg-white">
                          No questions yet — add one below.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {scenario.questions.map((q, qIndex) => (
                            <LocalQuestionEditor
                              key={q.tempId}
                              index={qIndex}
                              total={scenario.questions.length}
                              value={q}
                              onChange={(next) =>
                                editQuestion(sIndex, qIndex, next)
                              }
                              onRemove={() => removeQuestion(sIndex, qIndex)}
                              onMove={(dir) => moveQuestion(sIndex, qIndex, dir)}
                            />
                          ))}
                        </div>
                      )}

                      {/* Add question */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                        <select
                          value={newType[scenario.tempId] ?? "mcq"}
                          onChange={(e) =>
                            setNewType((t) => ({
                              ...t,
                              [scenario.tempId]: e.target
                                .value as CBQQuestionType,
                            }))
                          }
                          className="h-11 px-3 border border-[#4379EE]/30 bg-white rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#4379EE]/40"
                        >
                          {QUESTION_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => addQuestion(sIndex)}
                          className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg border-2 border-dashed border-[#4379EE]/40 text-[#4379EE] text-sm font-semibold hover:bg-[#4379EE]/5 transition-colors"
                        >
                          <Plus size={16} /> Add Question
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add scenario */}
          <button
            type="button"
            onClick={addScenario}
            className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-2xl border-2 border-dashed border-[#4379EE]/40 text-[#4379EE] font-semibold hover:bg-[#4379EE]/5 transition-colors"
          >
            <Plus size={18} /> Add Scenario
          </button>
        </section>
      </div>
    );
  }
);

CBQBuilder.displayName = "CBQBuilder";

export default CBQBuilder;
